import { useQuery, useMutation } from "@tanstack/react-query";
import { apiClient } from "@shared/lib/axios";
import { clinicApiPath } from "@/shared/lib/apiPath";

// ==================== Types ====================

export interface MedicalAidInsurer {
  id: number;
  sub_number: number;
  insurer_number: string;
  insurer_name: string;
  insurer_type_id: number;
  insurer_type?: { id: number; name: string } | null;
  cost_rate: number | null;
  family_cost_rate: number | null;
  is_special: boolean;
  is_nonoutput_rezept: boolean;
  is_partial_aid: boolean;
  is_enabled_invoice?: boolean;
  /** Present when API returns claim columns (Clinic InsurerResource) */
  medix_claim_type_id?: number | null;
  /** Legacy aggregate hint; optional — computed client-side when absent */
  claim_type_id?: number | null;
  insurer_cnt?: number;
  address1?: string | null;
  application_start_date?: string;
  application_end_date?: string;
  effective_from?: string;
  effective_to?: string;
  available_from?: string | null;
  available_to?: string | null;
  calc_type?: number | null;
  limit_amount?: number | null;
  limit_num_month?: number | null;
  limit_amount_month?: number | null;
}

export interface Prefecture {
  id: number;
  name: string;
}

interface InsurersSearchResponse {
  success: boolean;
  data: {
    data: MedicalAidInsurer[];
  };
}

interface PrefecturesResponse {
  success: boolean;
  data: {
    prefectures: Prefecture[];
  };
}

// ==================== Hooks ====================

/**
 * Fetch prefectures for medical aid search
 */
export function useMedicalAidPrefectures(
  siteType: string,
  clinicNumber: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["medicalaid-prefectures", siteType, clinicNumber],
    queryFn: async () => {
      const response = await apiClient.get<PrefecturesResponse>(
        clinicApiPath(siteType, clinicNumber, '/addresses/prefectures'),
      );
      return response.data.data.prefectures || [];
    },
    enabled,
  });
}

/**
 * Search medical aid insurers by type and prefecture
 */
export function useSearchMedicalAidInsurers(
  siteType: string,
  clinicNumber: string,
) {
  return useMutation({
    mutationFn: async (params: {
      insurer_type_id?: number;
      prefectures_name?: string;
      insurer_number?: string;
      // A2 No259: exact match for modal global lookup
      exact_insurer_number?: string;
      // Short-circuit with LIMIT 2 when the caller only acts on a unique match
      abort_if_multiple?: boolean;
    }) => {
      // Laravel's `boolean` rule rejects the string "true"/"false" axios emits for JS booleans,
      // so serialize to 1/0 explicitly.
      const { abort_if_multiple, ...rest } = params;
      const response = await apiClient.get<InsurersSearchResponse>(
        clinicApiPath(siteType, clinicNumber, '/insurers/search'),
        {
          params: {
            ...rest,
            ...(abort_if_multiple != null ? { abort_if_multiple: abort_if_multiple ? 1 : 0 } : {}),
          },
        },
      );
      return response.data.data.data || [];
    },
  });
}

/**
 * Load all insurer rows (枝番 intervals) for a medical-aid number + type (no prefecture filter).
 */
export function useFetchMedicalAidIntervalRows(siteType: string, clinicNumber: string) {
  return useMutation({
    mutationFn: async (params: { insurer_type_id: number; insurer_number: string }) => {
      const response = await apiClient.get<InsurersSearchResponse>(
        clinicApiPath(siteType, clinicNumber, '/insurers/search'),
        {
          params: {
            insurer_type_id: params.insurer_type_id,
            // A2 No962: exact match — avoids LIKE '%114%' returning 1140, 1141, etc.
            exact_insurer_number: params.insurer_number.trim(),
          },
        },
      );
      return response.data.data.data || [];
    },
  });
}
